// client/src/v2/baixar-app.tsx
// ────────────────────────────────────────────────────────────────────────
// BaixarApp — oferece o app nativo logo depois do cadastro.
//
// Por que aqui e nao depois: o produto e HABITO, e habito depende de
// lembrete. Lembrete so existe no app nativo (push). Quem fica so no web
// tem chance bem menor de chegar ao fim dos 7 dias — entao o melhor momento
// pra pedir a instalacao e agora, com a pessoa ja comprometida.
//
// Estado das lojas em 31/10/2026: as duas publicadas (a listagem do Google
// Play responde 200). Antes (19/08) o Android estava em teste fechado e
// aparecia como "em breve".
//
// LojasDoApp (31/10/2026): os dois botões das lojas, usados onde o site
// VENDE — o Pro se assina só pelas lojas (sem Mercado Pago na interface nova).
// ────────────────────────────────────────────────────────────────────────
import React from 'react';
import { isNativeApp } from './platform';
import { lerOrigem } from './origem';

export const APPLE_URL = 'https://apps.apple.com/br/app/forjamente/id6767300513';
export const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.forjamente.app';

/** A linha de quem quer o Pro no site (o preço vem depois, de plans.ts). */
export const LINHA_SO_LOJAS = 'Baixe o Forjamente, entre com a mesma conta e assine lá.';

export function plataformaDoAparelho(): 'ios' | 'android' | 'desktop' {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

/** O botão da loja passa por /baixar, que registra de onde veio o clique e
 *  redireciona para APPLE_URL / PLAY_URL (server/lojas). Os utm_ da primeira
 *  visita seguem junto. */
export function linkDaLoja(de: string, loja?: 'ios' | 'android', caminho = '/baixar'): string {
  const p = new URLSearchParams();
  if (loja) p.set('loja', loja);
  p.set('de', de);
  const o = lerOrigem();
  if (o?.utm_source) p.set('utm_source', o.utm_source);
  if (o?.utm_medium) p.set('utm_medium', o.utm_medium);
  if (o?.utm_campaign) p.set('utm_campaign', o.utm_campaign);
  if (o?.referrer) p.set('origem_ref', o.referrer);
  return caminho + '?' + p.toString();
}

const IconeApple = () => (
  <svg width="15" height="17" viewBox="0 0 24 28" fill="currentColor" aria-hidden="true">
    <path d="M17 14.6c0-3 2.4-4.3 2.5-4.4-1.4-2-3.5-2.3-4.2-2.3-1.8-.2-3.5 1-4.4 1s-2.3-1-3.8-1C5 8 3 9.6 1.9 11.8c-1.9 3.3-.5 8.2 1.4 10.9.9 1.3 2 2.8 3.4 2.7 1.4-.1 1.9-.9 3.5-.9s2.1.9 3.5.9c1.5 0 2.4-1.3 3.3-2.6.7-1 1.3-2.1 1.6-3.2-.1 0-3.1-1.2-3.1-4.9z"/>
    <path d="M14.5 6.1c.8-1 1.3-2.3 1.2-3.6-1.1.1-2.5.8-3.3 1.8-.7.8-1.4 2.1-1.2 3.3 1.2.1 2.5-.6 3.3-1.5z"/>
  </svg>
);
const IconePlay = () => (
  <svg width="13" height="16" viewBox="0 0 24 26" fill="currentColor" aria-hidden="true">
    <path d="M3 2.2v21.6c0 .5.5.8.9.5l11.4-10.8c.3-.3.3-.7 0-1L3.9 1.7c-.4-.3-.9 0-.9.5z"/>
  </svg>
);

/** Os dois botões das lojas. No celular, a loja do aparelho vem primeiro e em
 *  destaque (cor de brasa); a outra fica ao lado, discreta. No computador, os
 *  dois iguais. `escuro` = sobre fundo escuro (paywall). */
export const LojasDoApp: React.FC<{ de: string; escuro?: boolean }> = ({ de, escuro = false }) => {
  const plataforma = plataformaDoAparelho();
  const ordem: ('ios' | 'android')[] = plataforma === 'android' ? ['android', 'ios'] : ['ios', 'android'];
  const neutro = escuro
    ? { background: '#F5F1EA', color: '#1A1614' }
    : { background: 'var(--ink)', color: 'var(--paper)' };
  return (
    <div data-lojas-do-app={plataforma} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {ordem.map((loja) => {
        const destaque = loja === plataforma;
        return (
          <a key={loja} data-loja={loja} data-loja-destaque={destaque ? '1' : undefined}
            href={linkDaLoja(de, loja)} style={{
              flex: 1, minWidth: 130, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '12px 14px', borderRadius: 12, textDecoration: 'none',
              fontSize: 14, fontWeight: 600, boxSizing: 'border-box',
              ...(destaque ? { background: '#E87A3B', color: '#FFF8F0' } : neutro),
            }}>
            {loja === 'ios' ? <IconeApple /> : <IconePlay />}
            {loja === 'ios' ? 'App Store' : 'Google Play'}
          </a>
        );
      })}
    </div>
  );
};

type Props = {
  /** Texto acima dos botões. */
  titulo?: string;
  /** Some no desktop — útil quando a instalação não faz sentido ali. */
  soMobile?: boolean;
};

export const BaixarApp: React.FC<Props> = ({
  titulo = 'Baixe o app para receber seus lembretes',
  soMobile = false,
}) => {
  // Dentro do app nativo isso não faz sentido nenhum.
  if (typeof window === 'undefined' || isNativeApp()) return null;

  const ua = navigator.userAgent || '';
  const ehIOS = /iphone|ipad|ipod/i.test(ua);
  const ehAndroid = /android/i.test(ua);
  const ehMobile = ehIOS || ehAndroid;
  if (soMobile && !ehMobile) return null;

  // No Android sem loja publicada não há o que oferecer: melhor não mostrar
  // um bloco inteiro que termina em "em breve".
  if (ehAndroid && !PLAY_URL) {
    return (
      <div style={{
        marginTop: 18, padding: '12px 14px', borderRadius: 12,
        background: 'var(--paper-2)', border: '1px solid var(--line)',
        fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5, textAlign: 'center',
      }}>
        O app de Android está em testes. Por enquanto, use pelo navegador —
        funciona igual.
      </div>
    );
  }

  return (
    <div style={{
      marginTop: 18, padding: '16px 16px 14px', borderRadius: 14,
      background: 'var(--paper-2)', border: '1px solid var(--line)',
    }}>
      <div style={{
        fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5,
        textAlign: 'center', marginBottom: 12,
      }}>{titulo}</div>

      <div style={{
        display: 'flex', gap: 9, justifyContent: 'center', flexWrap: 'wrap',
      }}>
        <a href={APPLE_URL} target="_blank" rel="noopener noreferrer" style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '11px 16px', borderRadius: 12, textDecoration: 'none',
          background: 'var(--ink)', color: 'var(--paper)',
          fontSize: 13.5, fontWeight: 600,
        }}>
          <svg width="15" height="17" viewBox="0 0 24 28" fill="currentColor" aria-hidden="true">
            <path d="M17 14.6c0-3 2.4-4.3 2.5-4.4-1.4-2-3.5-2.3-4.2-2.3-1.8-.2-3.5 1-4.4 1s-2.3-1-3.8-1C5 8 3 9.6 1.9 11.8c-1.9 3.3-.5 8.2 1.4 10.9.9 1.3 2 2.8 3.4 2.7 1.4-.1 1.9-.9 3.5-.9s2.1.9 3.5.9c1.5 0 2.4-1.3 3.3-2.6.7-1 1.3-2.1 1.6-3.2-.1 0-3.1-1.2-3.1-4.9z"/>
            <path d="M14.5 6.1c.8-1 1.3-2.3 1.2-3.6-1.1.1-2.5.8-3.3 1.8-.7.8-1.4 2.1-1.2 3.3 1.2.1 2.5-.6 3.3-1.5z"/>
          </svg>
          App Store
        </a>

        {PLAY_URL ? (
          <a href={PLAY_URL} target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '11px 16px', borderRadius: 12, textDecoration: 'none',
            background: 'var(--ink)', color: 'var(--paper)',
            fontSize: 13.5, fontWeight: 600,
          }}>
            <svg width="13" height="16" viewBox="0 0 24 26" fill="currentColor" aria-hidden="true">
              <path d="M3 2.2v21.6c0 .5.5.8.9.5l11.4-10.8c.3-.3.3-.7 0-1L3.9 1.7c-.4-.3-.9 0-.9.5z"/>
            </svg>
            Google Play
          </a>
        ) : (
          <span style={{
            display: 'inline-flex', alignItems: 'center', padding: '11px 16px',
            borderRadius: 12, border: '1px solid var(--line-strong)',
            color: 'var(--ink-4)', fontSize: 13,
          }}>Android em breve</span>
        )}
      </div>

      <div style={{
        fontSize: 11.5, color: 'var(--ink-4)', textAlign: 'center',
        marginTop: 10, lineHeight: 1.45,
      }}>Também funciona pelo navegador — o app só acrescenta os lembretes.</div>
    </div>
  );
};
